import { Building2, Check, ChevronsUpDown } from "lucide-react"
import { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { enterAdmin } from "../libs/admin-entry"
import { customerOverviewPath } from "../libs/customer-paths"
import { Button } from "./ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"

interface CustomerSwitcherProps {
  /**
   * "default" — the standalone outlined dropdown button.
   * "crumb" — a compact breadcrumb-style trigger for the top bar (renders the
   * customer name as static text when there's nothing to switch to).
   */
  variant?: "default" | "crumb"
}

export function CustomerSwitcher({ variant = "default" }: CustomerSwitcherProps) {
  const navigate = useNavigate()
  const { customerId } = useParams<{ customerId: string }>()
  const { accessibleCustomers, user } = useAuth()
  const [searchQuery, setSearchQuery] = useState("")
  const isCrumb = variant === "crumb"

  // Find current customer
  const currentCustomer = useMemo(() => {
    return accessibleCustomers.find((c) => c.customer?.id === customerId)?.customer
  }, [accessibleCustomers, customerId])

  // Filter customers based on search, alphabetized by name
  const filteredCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const matched = query
      ? accessibleCustomers.filter(
          ({ customer }) =>
            customer?.name?.toLowerCase().includes(query) ||
            customer?.externalRef?.toLowerCase().includes(query)
        )
      : accessibleCustomers
    return [...matched].sort((a, b) =>
      (a.customer?.name ?? "").localeCompare(b.customer?.name ?? "", undefined, {
        numeric: true,
        sensitivity: "base",
      })
    )
  }, [accessibleCustomers, searchQuery])

  const handleCustomerSelect = (newCustomerId: string) => {
    if (newCustomerId !== customerId) {
      // Store selected customer
      localStorage.setItem("last_selected_customer", newCustomerId)

      // Navigate to new customer's overview
      navigate(customerOverviewPath(newCustomerId))
    }
  }

  const canSwitch = !!customerId && accessibleCustomers.length > 1

  // Crumb with nothing to switch to: show the name as static text (or nothing).
  if (isCrumb && !canSwitch) {
    return currentCustomer?.name ? (
      <span className="block truncate text-[11.5px] tracking-[0.04em] text-content-3">
        {currentCustomer.name}
      </span>
    ) : null
  }

  // Default variant: don't show the switcher if there's nothing to switch to.
  if (!isCrumb && !canSwitch) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {isCrumb ? (
          <button
            type="button"
            aria-label="Switch customer"
            className="btn-unstyled flex max-w-[220px] items-center gap-1 text-[11.5px] tracking-[0.04em] text-content-3 transition-colors hover:text-content-2"
          >
            <span className="truncate">{currentCustomer?.name || "Select customer"}</span>
            <ChevronsUpDown className="size-3 shrink-0" />
          </button>
        ) : (
          <Button
            variant="outline"
            className="min-w-[200px] justify-between gap-2"
            aria-label="Switch customer"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Building2 className="size-4 flex-shrink-0 text-content-3" />
              <span className="truncate text-sm font-medium">
                {currentCustomer?.name || "Select Customer"}
              </span>
            </div>
            <ChevronsUpDown className="size-4 flex-shrink-0 text-content-3" />
          </Button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[300px]">
        {/* Search Input */}
        <div className="px-2 py-2">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-control border border-hairline-strong bg-surface px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-accent"
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        <DropdownMenuSeparator />

        {/* Customer List */}
        <div className="max-h-[300px] overflow-y-auto">
          {filteredCustomers.length === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-content-2">No customers found</div>
          ) : (
            filteredCustomers.map(({ customer }) => {
              if (!customer) return null
              return (
                <DropdownMenuItem
                  key={customer.id}
                  onClick={() => handleCustomerSelect(customer.id)}
                  className="cursor-pointer"
                >
                  <div className="flex w-full items-center justify-between">
                    <div className="flex min-w-0 items-center gap-2">
                      <Building2 className="size-4 flex-shrink-0 text-content-3" />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium">{customer.name}</span>
                        {customer.externalRef && (
                          <span className="truncate text-xs text-content-3">
                            Ref: {customer.externalRef}
                          </span>
                        )}
                      </div>
                    </div>
                    {customer.id === customerId && (
                      <Check className="size-4 flex-shrink-0 text-accent" />
                    )}
                  </div>
                </DropdownMenuItem>
              )
            })
          )}
        </div>

        {/* Admin Link */}
        {user?.isGlobalAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => enterAdmin()}
              className="cursor-pointer text-content-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm">Administration</span>
              </div>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
